import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// =============================================================================
// MenuAcoes — menu de ações que NÃO é cortado por overflow de card/tabela.
// Renderiza no <body> via createPortal (position:fixed) + posiciona pelo
// getBoundingClientRect do botão. Segue os padrões centrao-docs:
//   • 06-frontend-react §"Click-outside: mousedown, NUNCA click"
//   • 06-frontend-react §"Tooltip/menu em tabela exige createPortal"
//
// BUG que isto corrige: o handler de "clicar fora" precisa ignorar cliques
// tanto no BOTÃO quanto no MENU (o menu vive no portal, fora do botão). Sem a
// ref do menu, o mousedown fecha o menu ANTES do onClick do item disparar —
// e a ação nunca acontece. Por isso há DUAS refs (botão + menu).
// =============================================================================
export default function MenuAcoes({ itens = [], rotulo = 'Ações', largura = 240 }) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, paraCima: false });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const recalcular = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const itensReais = itens.filter(Boolean);
    const alturaEstim = itensReais.reduce((h, it) => h + (it.divisor ? 9 : 40), 12);
    const espacoAbaixo = window.innerHeight - r.bottom;
    const paraCima = espacoAbaixo < alturaEstim && r.top > espacoAbaixo;
    setPos({
      left: Math.max(8, Math.min(r.right - largura, window.innerWidth - largura - 8)),
      top: paraCima ? r.top - 6 : r.bottom + 6,
      paraCima,
    });
  }, [itens, largura]);

  // Fecha ao clicar FORA do botão E do menu (mousedown), no ESC, ao rolar/resize.
  useEffect(() => {
    if (!aberto) return;
    const onMouseDown = (e) => {
      const dentroBotao = btnRef.current?.contains(e.target);
      const dentroMenu = menuRef.current?.contains(e.target);
      if (!dentroBotao && !dentroMenu) setAberto(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setAberto(false); };
    const fecharEmMovimento = () => setAberto(false);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('touchstart', onMouseDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', fecharEmMovimento, true);
    window.addEventListener('resize', fecharEmMovimento);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('touchstart', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', fecharEmMovimento, true);
      window.removeEventListener('resize', fecharEmMovimento);
    };
  }, [aberto]);

  const toggle = () => {
    if (!aberto) recalcular();
    setAberto(v => !v);
  };

  const acionar = (it) => {
    setAberto(false);
    // Deixa o menu fechar antes de abrir um SweetAlert (evita conflito de foco)
    setTimeout(() => it.onClick?.(), 0);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`btn btn-sm btn-outline-secondary ${aberto ? 'active' : ''}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={aberto}
      >
        {rotulo} <i className={`bi bi-chevron-${aberto ? 'up' : 'down'}`} style={{ fontSize: '0.7rem' }} />
      </button>
      {aberto && createPortal(
        <div
          ref={menuRef}
          className="menu-acoes-portal"
          role="menu"
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            width: largura,
            transform: pos.paraCima ? 'translateY(-100%)' : 'none',
            zIndex: 3000,
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
            border: '1px solid #e3e7e3',
            padding: 6,
            animation: 'menuAcoesIn .12s ease-out',
          }}
        >
          {itens.filter(Boolean).map((it, i) => it.divisor ? (
            <div key={i} style={{ height: 1, background: '#eef1ee', margin: '5px 6px' }} />
          ) : (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => acionar(it)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                background: 'none', border: 'none', textAlign: 'left',
                padding: '10px 12px', borderRadius: 7, fontSize: '0.9rem',
                cursor: 'pointer', lineHeight: 1.2,
                color: it.perigo ? '#b53d3d' : (it.aviso ? '#b8860b' : '#2b2a27'),
              }}
              onMouseEnter={e => { e.currentTarget.style.background = it.perigo ? '#fdecea' : (it.aviso ? '#fef9e7' : '#f3f7f3'); }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <i className={`bi ${it.icone}`} style={{ fontSize: '1rem', flexShrink: 0 }} />
              <span>{it.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
